import { IFilterConfig } from '../../types'
import { EFilterType, EFilterSpecOperator, EFilterFetchConvertType } from '../../types/enum'
import { StylesConfig, GroupBase } from 'react-select'

type IReactSelectStyles<TOpt, TMulti extends boolean> = StylesConfig<TOpt, TMulti, GroupBase<TOpt>>

type ICustomStyles = {
	menuWidth?: string | number
}

export function reactSelectStyles<TOpt, TMulti extends boolean>(
	isThemeDark: boolean, customStyles?: ICustomStyles
): IReactSelectStyles<TOpt, TMulti> {
	const scrollSize = '8px'
	const color = isThemeDark ? `var(--whiteColor)` : `var(--darkBodyBgColor)`
	const hoverColor = isThemeDark ? `var(--darkBodyBgColor)` : `var(--whiteColor)`
	const bgColor = isThemeDark ? `var(--darkBodyBgColor)` : `var(--whiteColor)`
	const multiBgColor = isThemeDark ? `var(--greyColor)` : `var(--greyColor)`
	const borderColor = isThemeDark ? `var(--darkBorderColor)` : `var(--formBorderColor09)`
	const menuBorderColor = isThemeDark ? `var(--darkBorderColor01)` : `var(--formBorderColor09)`
	const singleValueColor = isThemeDark ? `var(--whiteColor)` : `var(--darkBodyBgColor)`

	const menuWidth = customStyles?.menuWidth

	return ({
		control: (provided, state) => {
			//console.log('SingleSelect', {provided, state})
			return ({
				...provided,
				cursor: 'pointer',
				transition: `var(--transition)`,
				minHeight: `var(--form-control-height)`,
				backgroundColor: isThemeDark ?
					(state.isDisabled ? `var(--darkDisabledBgColor)` : `var(--darkBodyBgColor)`) :
					provided.backgroundColor,
				borderColor: borderColor,
				'&:hover': {
					borderColor: borderColor
				}
			})
		},
		option: (provided, state) => {
			return {
				...provided,
				cursor: 'pointer',
				transition: `var(--transition)`,
				backgroundColor: state.isSelected ? `var(--mainColor)` : bgColor, // Change background when selected
				color: state.isSelected ? `var(--whiteColor)` : color, // Change text color when selected
				'&:hover': { // Optional: Add hover styles
					backgroundColor: state.isSelected ? `var(--mainColor)` : 'lightblue',
					color: state.isSelected ? `var(--whiteColor)` : `var(--darkBodyBgColor)`
					//color: state.isSelected ? `var(--whiteColor)` : hoverColor
				}
			}
		},
		menu: (provided) => ({
			...provided,
			transition: `var(--transition)`,
			backgroundColor: bgColor, // Background for the dropdown menu,
			border: isThemeDark ? `1px solid ${menuBorderColor}` : provided.border,
			...(!!menuWidth && { width: menuWidth })
		}),
		menuList: (provided) => ({
			...provided,
			'::-webkit-scrollbar': {
				width: scrollSize,
				height: '0px',
				background: isThemeDark ? '#EEEEEE08' : '#fafafa'
			},
			'::-webkit-scrollbar-track': {
				boxShadow: isThemeDark ? 'unset' : 'inset 0 0 2px grey',
				background: isThemeDark ? '#EEEEEE08' : 'unset'
				//borderRadius: scrollSize
			},
			'::-webkit-scrollbar-thumb': {
				background: isThemeDark ? 'unset' : '#c7c7c7',
				boxShadow: isThemeDark ? 'inset 0 0 6px rgba(255, 255, 255, 0.3)' : 'unset',
				borderRadius: scrollSize
			},
			'::-webkit-scrollbar-thumb:hover': {
				background: isThemeDark ? 'unset' : '#878787',
				boxShadow: isThemeDark ? 'inset 0 0 6px rgba(255, 255, 255, 0.6)' : 'unset'
			}
		}),
		singleValue: (provided, state) => ({
			...provided,
			transition: `var(--transition)`,
			// TODO: check for different color icons?
			color: isThemeDark ?
				(state.isDisabled ? `var(--disabledColor)` : `var(--whiteColor)`) :
				provided.color,
			backgroundColor: isThemeDark ?
				(state.isDisabled ? `var(--darkDisabledBgColor)` : `var(--darkBodyBgColor)`) :
				provided.backgroundColor
		}),
		multiValue: (provided, state) => ({
			...provided,
			transition: `var(--transition)`,
			// TODO: check for different color icons?
			//color: isThemeDark ? 'var(--whiteColor)' : provided.color,
			backgroundColor: isThemeDark ? 'var(--greyColor01)' : provided.backgroundColor
			//backgroundColor: isThemeDark ? 'var(--mainColor01)' : provided.backgroundColor
		}),
		multiValueLabel: (provided) => ({
			...provided,
			transition: `var(--transition)`,
			color: isThemeDark ? 'var(--whiteColor)' : provided.color
		}),
		multiValueRemove: (provided) => ({
			...provided,
			transition: `var(--transition)`
			// color: isThemeDark ? `var(--dangerColorHover)` : provided.color,
			// '&:hover': {
			// 	color: isThemeDark ? `var(--dangerColorHover)` : provided[':hover']?.color,
			// },
		}),
		valueContainer: (provided, state) => ({
			...provided,
			transition: `var(--transition)`,
			color: singleValueColor,
			opacity: state.isDisabled ? '0.75' : provided.opacity
		}),
		input: (provided) => ({
			...provided,
			transition: `var(--transition)`,
			color: color
		}),
		clearIndicator: (provided, state) => ({
			...provided,
			transition: `var(--transition)`,
			color: isThemeDark ? `var(--dangerColor)` : provided.color,
			'&:hover': {
				color: isThemeDark ? `var(--dangerColorHover)` : provided[':hover']?.color
			}
		}),
		dropdownIndicator: (provided, state) => ({
			...provided,
			transition: `var(--transition)`,
			color: isThemeDark ? (state.isFocused ? `var(--whiteColor1)` : `var(--bodyColor)`) : provided.color,
			'&:hover': {
				color: isThemeDark ? `var(--whiteColor1)` : provided[':hover']?.color
			}
		})
	})
}

export function constructUserFiltersFromUrl<T>(
	urlFilter: [string, any][],
	config: IFilterConfig<keyof T>[],
	filtersDefaultValue: T
): T {
	const newUserFilters: T = { ...filtersDefaultValue }

	if (!!urlFilter && urlFilter.length > 0) {
		urlFilter.forEach(([itemNameWithOperator, itemValue]) => {
			const foundItem = config.find(configItem => {
				return `${String(configItem.name)}${configItem.operator}` === itemNameWithOperator
			})
			//console.log('************** constructUserFiltersFromUrl:: ', { foundItem, itemValue })
			//TODO: check the condition for new data structure/types
			// currently processing: string, Array, number
			if (!!foundItem && (itemValue?.length > 0 || itemValue?.toString()?.length > 0)) {
				const fieldName = foundItem.name
				const configItemType = foundItem?.type
				const configItemFetchConvertType = foundItem?.fetchConvertType

				if (configItemType === EFilterType.selectSingle &&
					configItemFetchConvertType === EFilterFetchConvertType.boolean &&
					typeof itemValue === 'boolean'
				) {
					// special converting (booleanToString) because react-select works only with 'string' data
					// as any - because of "Type string is not assignable to type 'T[keyof T]'"
					newUserFilters[fieldName] = itemValue?.toString() as any
				} else {
					newUserFilters[fieldName] = foundItem?.specOperator === EFilterSpecOperator.everywhere ?
						itemValue.slice(1, -1) : itemValue
				}
			}
		})
	}
	//console.log('++++++++++++ constructUserFiltersFromUrl END:: ', { urlFilter, newUserFilters })
	return newUserFilters
}

export function constructNewFilterFromConfig<T>(
	userFiltersState: T, config: IFilterConfig<keyof T>[]
): {
	newFilter: [string, any][];
	activeFiltersCnt: number;
} {
	const newFilter: [string, any][] = []
	let activeFiltersCnt = 0

	config.forEach(configItem => {
		const configItemFetchConvertType = configItem?.fetchConvertType
		const configItemName = configItem.name
		const configItemType = configItem.type
		const configItemNameStr = String(configItemName)
		const stateValue = userFiltersState[configItemName]

		switch (configItemType) {
			case EFilterType.date: {
				if (stateValue) {
					const by = `${configItemNameStr}${configItem.operator}`
					newFilter.push([by, stateValue])
					activeFiltersCnt++
				}
				break
			}
			case EFilterType.dateRange: {
				if (Array.isArray(stateValue) && stateValue.every(_ => !!_)) {
					const by = `${configItemNameStr}${configItem.operator}`
					newFilter.push([by, stateValue])
					activeFiltersCnt++
				}
				break
			}
			case EFilterType.input: {
				if (stateValue) {
					const by = `${configItemNameStr}${configItem.operator}`

					//TODO: rewrite - implement separate [via configItem] validation for each FilterType or field
					if (configItemFetchConvertType === EFilterFetchConvertType.number) {
						const value: number | null = parseInt(stateValue as string) > 0 ? parseInt(stateValue as string) : null
						newFilter.push([by, value])
					} else if (configItemFetchConvertType === EFilterFetchConvertType.numberArray) {
						const value: number[] | null = parseInt(stateValue as string) > 0 ? [parseInt(stateValue as string)] : null
						newFilter.push([by, value])
					} else {
						if (configItem?.specOperator === EFilterSpecOperator.everywhere) {
							// TODO: check экранирование для '*' и '%' символов введенных пользователем
							newFilter.push([by, `%${stateValue}%`])
						} else {
							newFilter.push([by, stateValue])
						}
					}
					activeFiltersCnt++
				}
				break
			}
			case EFilterType.numberRange: {
				if (Array.isArray(stateValue) && stateValue.every(_ => !!_ && Number.isFinite(+_))) {
					const by = `${configItemNameStr}${configItem.operator}`
					const value: number[] = stateValue.map(_ => !!_ ? parseInt(_) : 0)
					newFilter.push([by, value])
					activeFiltersCnt++
				}
				break
			}
			case EFilterType.selectSingle: {
				if (stateValue) {
					const by = `${configItemNameStr}${configItem.operator}`

					if (configItemFetchConvertType === EFilterFetchConvertType.boolean) {
						// special back-converting (StringToBoolean) because react-select works only with 'string' data
						const value: boolean = stateValue === 'true'
						newFilter.push([by, value])
					} else {
						newFilter.push([by, stateValue])
					}

					activeFiltersCnt++
				}
				break
			}
			case EFilterType.selectMultiple: {
				if (Array.isArray(stateValue) && stateValue.length > 0) {
					const by = `${configItemNameStr}${configItem.operator}`
					newFilter.push([by, stateValue])
					activeFiltersCnt++
				}
				break
			}
			default: {
				throw new Error(`filtersConfig for name=${configItemNameStr} with type=${configItemType} is NOT valid`)
			}
		}
	})

	return {
		newFilter,
		activeFiltersCnt
	}
}